import { useCallback, useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";

// PLEX-6742: a scroll is the user's only within this long of their input (a wheel turn, a
// touch, a scrolling key) or while their pointer is held anywhere on the scroller: dragging its
// scrollbar, or selecting text past its edge, which scrolls it too. That also counts a browser
// move that happens during such a press, which errs toward leaving the user's view alone. The
// browser also moves scrollTop on its own: scroll anchoring, when content above the view
// changes, drops it exactly as the user scrolling up would.
const USER_INPUT_WINDOW_MS = 1000;
const SCROLL_KEYS = new Set(["PageUp", "PageDown", "ArrowUp", "ArrowDown", "Home", "End", " "]);
const isEditable = el => !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || ""));

// Ring buffer of recent autoscroll/user-scroll events, for diagnosing scroll
// lockups (e.g. PLEX-6060) that don't reproduce on demand. Also mirrored to
// Sentry breadcrumbs so any event captured while a lockup is happening carries
// the sequence of scroll/autoscroll state transitions leading up to it.
const MAX_SCROLL_EVENTS = 50;

// Automatically scroll to the bottom of the container when the size or
// scrolling content changes, unless the user has started scrolling.
// Set onUserScroll to false to always scroll to bottom on component size
// changes, regardless of user activity.
// initiallyDisabled: if true, autoscroll is disabled until explicitly enabled
export const useAutoScroll = (onUserScroll = null, initiallyDisabled = false) => {
  const [container, setContainer] = useState(null);
  const ref = useCallback(node => setContainer(node), []);
  const tailRef = useRef();
  const isUserScrolling = useRef();
  const isAutoScrolling = useRef();
  const isAutoScrollDisabled = useRef(initiallyDisabled);
  const lastScrollHeight = useRef();
  const atBottomScrollTop = useRef();
  const scrollEvents = useRef([]);
  const lastUserInput = useRef(0);
  const pointerHeld = useRef(false);
  const byUser = () => pointerHeld.current || Date.now() - lastUserInput.current < USER_INPUT_WINDOW_MS;
  // scrollHeight and clientHeight are rounded to whole pixels and scrollTop isn't, so the furthest
  // a scroll can go may be a pixel short of scrollHeight - clientHeight (PLEX-6737)
  const isAtBottom = el => Math.abs(el.scrollTop + el.clientHeight - el.scrollHeight) <= 1;

  const logScrollEvent = (type, detail = {}) => {
    const entry = { t: Date.now(), type, ...detail };
    scrollEvents.current.push(entry);
    if (scrollEvents.current.length > MAX_SCROLL_EVENTS) {
      scrollEvents.current.shift();
    }
    Sentry.addBreadcrumb({ category: "autoscroll", message: type, level: "info", data: detail });
  };

  const getScrollDiagnostics = () => scrollEvents.current.slice();

  const scrollToBottom = (onComplete = null) => {
    if (!container || isAtBottom(container)) {
      return;
    }
    logScrollEvent("scrollToBottom", { scrollTop: container.scrollTop, scrollHeight: container.scrollHeight });
    isAutoScrolling.current = true;
    container.classList.add('auto-scrolling');

    // Check if any ancestor container is user-scrolling or auto-scrolling
    // If so, we need to avoid scrollIntoView which would scroll the ancestor
    const hasScrollingAncestor = container.parentElement?.closest('.user-scrolling, .auto-scrolling') !== null;

    // Use scrollIntoView only if no ancestor is scrolling (nice smooth scroll)
    // Otherwise use scrollTop to avoid triggering ancestor scroll and causing loops
    if (hasScrollingAncestor) {
      // Only scroll this container, don't trigger ancestor scrolling
      container.scrollTop = container.scrollHeight;
      // Immediately mark as complete since scrollTop is synchronous
      atBottomScrollTop.current = container.scrollTop;
      isAutoScrolling.current = false;
      container.classList.remove('auto-scrolling');
      onComplete && onComplete(container);
    } else {
      const startTop = container.scrollTop;
      tailRef.current?.scrollIntoView({ behavior: "smooth" });

      // Check to see if we're at the bottom yet. PLEX-6737: keep going only while the scroll is
      // still moving down. A scroll that has stopped short of the bottom used to retry every
      // 50 ms forever, and while it did, the scroll handler ignored the user (isAutoScrolling)
      // and each retry scrolled them back down.
      setTimeout(() => {
        if (container) {
          const top = container.scrollTop;
          // Content that shrinks also pulls scrollTop up (the browser clamps it to the new bottom),
          // so going up only means the user when it leaves us short of the bottom
          if (top < startTop && !isAtBottom(container) && byUser()) {
            // The user scrolled up while we scrolled down: stop and leave them there
            isAutoScrolling.current = false;
            container.classList.remove('auto-scrolling');
            if (!isUserScrolling.current) {
              isUserScrolling.current = true;
              container.classList.add('user-scrolling');
              logScrollEvent("userScrollingDuringAutoScroll", { scrollTop: top, from: startTop });
              onUserScroll && onUserScroll();
            }
            onComplete && onComplete(container);
          } else if (!isAtBottom(container) && top > startTop) {
            scrollToBottom(onComplete);
          } else {
            if (!isAtBottom(container)) {
              logScrollEvent("scrollToBottomStopped", { scrollTop: top, scrollHeight: container.scrollHeight, clientHeight: container.clientHeight });
            }
            atBottomScrollTop.current = top;
            isAutoScrolling.current = false;
            container.classList.remove('auto-scrolling');
            onComplete && onComplete(container);
          }
        }
      }, 50);
    }
  };

  const autoScroll = (force = false, onComplete = null) => {
    // Enable autoscroll on first manual call (e.g., when user sends a message)
    if (isAutoScrollDisabled.current && force) {
      isAutoScrollDisabled.current = false;
    }

    // Don't autoscroll if disabled (e.g., during initial conversation load)
    if (isAutoScrollDisabled.current) {
      onComplete && onComplete(container);
      return;
    }

    // Check if disable-autoscroll class is present (set externally, e.g., during prompt expansion)
    const isExternallyDisabled = container?.classList.contains('disable-autoscroll');

    if ((!isUserScrolling.current && !isExternallyDisabled) || force) {
      scrollToBottom(onComplete);
    }
    else {
      onComplete && onComplete(container);
    }
  };

  const scrollHandler = e => {
    if (e.target !== container) {
      return;
    }
    lastScrollHeight.current = container.scrollHeight;
    if (atBottomScrollTop.current === null) {
      atBottomScrollTop.current = container.scrollHeight - container.clientHeight;
    }
    const currentScrollTop = container.scrollTop;

    // Clear user scrolling only once the user is actually back at the bottom.
    // Scrollbar presence is incidental to content size, not user intent — a transient
    // shrink (remount, drawer transition, error-retry flicker) must not silently un-arm
    // this flag, or the next resize snaps the view to bottom out from under the user.
    if (isAtBottom(container)) {
      atBottomScrollTop.current = currentScrollTop;
      if (isUserScrolling.current) {
        isUserScrolling.current = false;
        container.classList.remove('user-scrolling');
      }
    }
    // Detect user scrolling up
    else if (!isAutoScrolling.current
             && byUser()
             && atBottomScrollTop.current != null
             && atBottomScrollTop.current > currentScrollTop) {
      if (!isUserScrolling.current) {
        isUserScrolling.current = true;
        container.classList.add('user-scrolling');
        logScrollEvent("userScrollingStarted", { scrollTop: currentScrollTop, atBottomScrollTop: atBottomScrollTop.current });
        onUserScroll && onUserScroll(e);
      }
    }
  };

  // Ensure we trigger autoscroll on component content/size change
  useEffect(() => {
    if (container) {
      lastScrollHeight.current = container.scrollHeight;
      container.addEventListener("scroll", scrollHandler);
      const markInput = () => { lastUserInput.current = Date.now(); };
      const onKeyDown = e => { if (SCROLL_KEYS.has(e.key) && !isEditable(e.target)) markInput(); };
      const onPointerDown = () => { pointerHeld.current = true; markInput(); };
      const onPointerUp = () => { if (pointerHeld.current) { pointerHeld.current = false; markInput(); } };
      const inputs = ["wheel", "touchstart", "touchmove"];
      inputs.forEach(type => container.addEventListener(type, markInput, {passive: true}));
      container.addEventListener("pointerdown", onPointerDown);
      window.addEventListener("pointerup", onPointerUp);
      window.addEventListener("pointercancel", onPointerUp);
      window.addEventListener("keydown", onKeyDown);

      // Use Intersection Observer to detect when user scrolls away from bottom
      // This is more efficient than MutationObserver which fires on every content change
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) {
              // Tail element is not visible - user has scrolled up or content is empty.
              // PLEX-6742: or the reply grew past the bottom. The tail is 0 px tall, so any growth
              // hides it, and this runs before the resize below can scroll to it: without the
              // user's input, that's content for the auto-scroll to follow.
              const overflows = container.scrollHeight > container.clientHeight;
              if (!isUserScrolling.current && (byUser() || !overflows)) {
                isUserScrolling.current = true;
                container.classList.add('user-scrolling');
                logScrollEvent("tailLeftView", { intersectionRatio: entry.intersectionRatio });
                onUserScroll && onUserScroll();
              }
            } else if (entry.isIntersecting && !isAutoScrolling.current) {
              // Tail is visible and we're not in the middle of auto-scrolling
              // User may be reading at the bottom - allow further scrolls to be auto
              if (isUserScrolling.current) {
                isUserScrolling.current = false;
                container.classList.remove('user-scrolling');
                logScrollEvent("tailReentredView", { intersectionRatio: entry.intersectionRatio });
              }
            }
          });
        },
        {
          root: container,
          threshold: 0.1,
        }
      );

      if (tailRef.current) {
        intersectionObserver.observe(tailRef.current);
      }

      let resizeTimeout;
      const resizeObserver = new ResizeObserver((entries) => {
        // Debounce to avoid "ResizeObserver loop completed with undelivered notifications"
        // Use requestAnimationFrame + setTimeout to ensure layout is complete
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          requestAnimationFrame(() => {
            try {
              if (!container) return;

              // Only auto-scroll if user hasn't scrolled up
              if (!isUserScrolling.current && !isAutoScrollDisabled.current) {
                logScrollEvent("resizeTriggeredAutoScroll", { scrollHeight: container.scrollHeight, entryCount: entries.length });
                autoScroll();
              }
            } catch (error) {
              // Suppress ResizeObserver errors - they're benign and don't affect functionality
              if (!error.message?.includes('ResizeObserver')) {
                console.error('Auto-scroll error:', error);
              }
            }
          });
        }, 10);
      });
      Array.from(container.children).forEach(child => resizeObserver.observe(child));
      resizeObserver.observe(container);
      // Initialize state
      autoScroll();

      return () => {
        // Defensive checks to prevent race conditions during unmount
        // Fixes WEBAPP-HY: React DOM node removal error when component unmounts
        // while observers are still processing
        if (container && container.parentNode) {
          container.removeEventListener("scroll", scrollHandler);
        }
        inputs.forEach(type => container.removeEventListener(type, markInput));
        container.removeEventListener("pointerdown", onPointerDown);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
        window.removeEventListener("keydown", onKeyDown);
        intersectionObserver.disconnect();
        resizeObserver.disconnect();
      };
    }
  }, [container, onUserScroll]);

  // Return the ref, tailRef, autoScroll function, enable function, and
  // scroll diagnostics getter (for attaching to bug reports)
  const enableAutoScroll = () => {
    isAutoScrollDisabled.current = false;
  };

  return [ref, tailRef, autoScroll, enableAutoScroll, getScrollDiagnostics];
};
